import { computed, inject, Injectable, isDevMode, OnDestroy, Signal } from '@angular/core';
import { FilterStore } from './filter.store';
import { DisplayLocation } from '@nha/enums/displayLocation.enum';
import { IFilter, IFilterGrouping, IFilterItem, IFiltersConfig, IFiltersInstance, IFilterSyncValue } from '../interfaces';
import { HttpClient } from '@angular/common/http';
import { Subject, Subscription, debounceTime, map, switchMap, take } from 'rxjs';
import { FilterStorageService } from './filter-storage.service';
import { NHASnackbarService } from '@nha/components/snack-bar';
import { FacetGroup, FacetSelections, facetKeys, tallyFacets, TallyResult } from '../counting';

/** Per-item badge state for one filter, keyed by `IFilterItem.id`. */
export type FilterItemCounts = Map<number, { count: number; disabled: boolean }>;

/**
 * Root-level filter state management service.
 *
 * Note: As a root-provided service, this lives for the application lifetime.
 * Subscriptions created in the constructor are intentionally long-lived.
 * The ngOnDestroy is implemented as a safeguard if the provider scope changes in the future.
 */
@Injectable({ providedIn: 'root' })
export class FilterStateService implements OnDestroy {
	private readonly filterStore = inject(FilterStore);
	private readonly http = inject(HttpClient);
	private readonly filterStorageService = inject(FilterStorageService);
	private readonly snackbarService = inject(NHASnackbarService);

	private readonly updateFilterSubject$ = new Subject<{ key: string; filterKey: string; filter: IFilter; requestId: string }>();
	private readonly bulkUpdateFiltersSubject$ = new Subject<{ key: string; filters: { [filterKey: string]: IFilter }; requestId: string }>();
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private readonly initFilterSubject$ = new Subject<{ url: string; body: any; key: string }>();
	private readonly updateFilterSubscription: Subscription;
	private readonly bulkUpdateFiltersSubscription: Subscription;
	private readonly initFilterSubscription: Subscription;
	private readonly latestRequestId = new Map<string, string>();
	private requestIdCounter = 0;

	/**
	 * One tally computed per instance key. Every accessor here mints a fresh computed on
	 * every call and several consumers wrap them — getFacetedResults and one getFilterCounts
	 * per rendered filter all read the tally — so an unmemoized version would re-run the
	 * whole tally pass, search predicate included, once per consumer per store change.
	 */
	private readonly tallyByKey = new Map<string, Signal<TallyResult<unknown> | null>>();
	/** Keyed `${key}|${filterKey}` — see {@link getFilterCounts}. */
	private readonly filterCountsByKey = new Map<string, Signal<FilterItemCounts | null>>();

	/** Instance keys already checked by {@link validateFacetRegistration} — see its doc comment. */
	private readonly validatedFacetKeys = new Set<string>();

	constructor() {
		this.updateFilterSubscription = this.updateFilterSubject$
			.pipe(
				debounceTime(150),
				switchMap(({ key, filterKey, filter, requestId }) => {
					const filtersInstance = this.filterStore.getByKey(key);
					if (!filtersInstance) {
						return [];
					}

					// Skip API call if no updateUrl configured (client-side filtering only)
					if (!filtersInstance.config.updateUrl) {
						this.filterStore.update(key, { updating: false, updated: true });
						return [];
					}

					const filterMap = this.filterStore.getFilterMap(key);
					if (!filterMap) {
						return [];
					}

					const updatedFilterMap = {
						...filterMap,
						[filterKey]: {
							...filterMap[filterKey],
							items: filter.items
						}
					};

					// Same invariant as the /results body: a client-side facet selection must never reach the
					// server on ANY outbound path (see withoutFacetSelections). A facet click never gets here
					// — updateFilter/updateFilters short-circuit to settleClientSideChange — but a change to a
					// SERVER-side filter (or a mixed batch) while a facet selection is active builds its
					// payload from the whole filterMap, which still carries that selection. Suppressed before
					// the reduce, so a faceted filter has no selected items left and drops out of the payload
					// entirely. Note `key` is shadowed inside the reduce below, so read the registry out here.
					const outboundFilterMap = this.withoutFacetSelections(updatedFilterMap, this.registeredFacetKeys(key));

					const selectedFiltersPayload = Object.entries(outboundFilterMap).reduce(
						(acc, [key, value]) => {
							if (value.items.some((i) => i.selected)) {
								acc[key] = value;
							}
							return acc;
						},
						{} as { [key: string]: IFilter }
					);

					const route = `${filtersInstance.config.baseUrl}${filtersInstance.config.updateUrl}`;

					// State already updated optimistically in updateFilter(), just make the API call
					return this.http.post<{ [key: string]: IFilter }>(route, selectedFiltersPayload).pipe(
						take(1),
						map((filterMap) => ({ filterMap, key, requestId }))
					);
				})
			)
			.subscribe({
				next: (value) => {
					// Only process if this is the latest request for this key
					if (this.latestRequestId.get(value.key) === value.requestId) {
						this.handleFilterResponse(value.filterMap, value.key, value.requestId);
					}
				},
				error: (error) => {
					console.error('Error updating filters:', error);
					this.snackbarService.error('Failed to update filter. Please try again.');
				}
			});

		this.bulkUpdateFiltersSubscription = this.bulkUpdateFiltersSubject$
			.pipe(
				debounceTime(150),
				switchMap(({ key, filters, requestId }) => {
					const filtersInstance = this.filterStore.getByKey(key);
					if (!filtersInstance) {
						return [];
					}

					// Skip API call if no updateUrl configured (client-side filtering only)
					if (!filtersInstance.config.updateUrl) {
						this.filterStore.update(key, { updating: false, updated: true });
						return [];
					}

					const filterMap = this.filterStore.getFilterMap(key);
					if (!filterMap) {
						return [];
					}

					// Merge all updated filters into the existing filter map
					const updatedFilterMap = {
						...filterMap,
						...Object.entries(filters).reduce(
							(acc, [filterKey, filter]) => {
								acc[filterKey] = {
									...filterMap[filterKey],
									items: filter.items
								};
								return acc;
							},
							{} as { [key: string]: IFilter }
						)
					};

					// Same invariant as the /results body: a client-side facet selection must never reach the
					// server on ANY outbound path (see withoutFacetSelections). A facet click never gets here
					// — updateFilter/updateFilters short-circuit to settleClientSideChange — but a change to a
					// SERVER-side filter (or a mixed batch) while a facet selection is active builds its
					// payload from the whole filterMap, which still carries that selection. Suppressed before
					// the reduce, so a faceted filter has no selected items left and drops out of the payload
					// entirely. Note `key` is shadowed inside the reduce below, so read the registry out here.
					const outboundFilterMap = this.withoutFacetSelections(updatedFilterMap, this.registeredFacetKeys(key));

					const selectedFiltersPayload = Object.entries(outboundFilterMap).reduce(
						(acc, [key, value]) => {
							if (value.items.some((i) => i.selected)) {
								acc[key] = value;
							}
							return acc;
						},
						{} as { [key: string]: IFilter }
					);

					const route = `${filtersInstance.config.baseUrl}${filtersInstance.config.updateUrl}`;

					// State already updated optimistically in updateFilters(), just make the API call
					return this.http.post<{ [key: string]: IFilter }>(route, selectedFiltersPayload).pipe(
						take(1),
						map((filterMap) => ({ filterMap, key, requestId }))
					);
				})
			)
			.subscribe({
				next: (value) => {
					// Only process if this is the latest request for this key
					if (this.latestRequestId.get(value.key) === value.requestId) {
						this.handleFilterResponse(value.filterMap, value.key, value.requestId);
					}
				},
				error: (error) => {
					console.error('Error bulk updating filters:', error);
					this.snackbarService.error('Failed to update filters. Please try again.');
				}
			});

		// Set up filter initialization with automatic cancellation
		this.initFilterSubscription = this.initFilterSubject$
			.pipe(
				switchMap(({ url, body, key }) =>
					this.http.post<{ [key: string]: IFilter }>(url, body).pipe(
						take(1),
						map((filterMap) => ({ filterMap, key }))
					)
				)
			)
			.subscribe({
				next: (value) => {
					const filtersInstance = this.filterStore.getByKey(value.key);
					if (filtersInstance) {
						this.handleFilterResponse(value.filterMap, value.key);
					}
				},
				error: (error) => {
					console.error('Error initializing filters:', error);
					this.snackbarService.error('Failed to load filters. Please refresh the page.');
				}
			});
	}

	/**
	 * Cleanup method for subscriptions and subjects.
	 * Note: This won't be called for root-provided services (expected behavior).
	 * Implemented as safeguard if provider scope changes in the future.
	 */
	ngOnDestroy(): void {
		this.updateFilterSubscription?.unsubscribe();
		this.bulkUpdateFiltersSubscription?.unsubscribe();
		this.initFilterSubscription?.unsubscribe();
		this.updateFilterSubject$.complete();
		this.bulkUpdateFiltersSubject$.complete();
		this.initFilterSubject$.complete();
	}

	initializeFilter(key: string): void {
		const existingFilterInstance = this.filterStore.getByKey(key);
		let initValues = this.filterStorageService.getInitValues(existingFilterInstance);

		const url = `${existingFilterInstance.config.baseUrl}${existingFilterInstance.config.initUrl}`;

		initValues = initValues.filter((x) => x.value !== null);

		// Mark loading before dispatch so callers using the loaded/loading guard
		// (e.g. ApplicationMenuComponent.initialize*FiltersIfNeeded) won't fire a
		// duplicate request while this one is in flight.
		this.filterStore.update(key, { loading: true, loaded: false });

		this.initFilterSubject$.next({ url, body: initValues, key });
	}

	deInitializeFilter(key: string): void {
		// Deleted unconditionally: resetFilter() removes the store entry before calling
		// initializeFilter(), so a deInit reached via that path would otherwise early-return
		// below and leak both memo entries for record-scoped dynamic keys.
		this.tallyByKey.delete(key);
		// The counts memo is per filter within the instance, so every entry for this
		// instance has to go, not just one.
		for (const memoKey of this.filterCountsByKey.keys()) {
			if (memoKey.startsWith(`${key}|`)) {
				this.filterCountsByKey.delete(memoKey);
			}
		}
		this.validatedFacetKeys.delete(key);

		const filterInstance = this.filterStore.getByKey(key);
		if (!filterInstance) {
			return;
		}

		if (filterInstance.config.keepSessionState === false) {
			this.filterStorageService.removeFilter(filterInstance.key);
		} else {
			const changes = { loaded: false, loading: false, updating: false, updated: false };
			this.filterStorageService.setFilter(filterInstance.key, { ...filterInstance, ...changes });
		}

		this.filterStore.remove(filterInstance.key);
	}

	updateFilter(key: string, filterKey: string, filter: IFilter): void {
		const filtersInstance = this.filterStore.getByKey(key);

		// Optimistic UI update - update state immediately for instant feedback
		const updatedFilterMap = filtersInstance?.filterMap
			? {
					...filtersInstance.filterMap,
					[filterKey]: { ...filtersInstance.filterMap[filterKey], items: filter.items }
				}
			: null;

		// A registered predicate means no round trip, so this must not consume a
		// request id — doing so would invalidate an in-flight scope response that
		// nothing would ever re-request.
		if (updatedFilterMap && this.isClientSideFilter(key, filterKey)) {
			this.settleClientSideChange(key, updatedFilterMap, filterKey);
			return;
		}

		// Generate unique request ID to track this specific update
		const requestId = `${key}_${++this.requestIdCounter}`;
		this.latestRequestId.set(key, requestId);

		if (updatedFilterMap) {
			this.filterStore.update(key, { updating: true, updated: false, filterMap: updatedFilterMap, lastChangedKey: filterKey });
		}

		// Emit the update request to the debounced subject for API call
		this.updateFilterSubject$.next({ key, filterKey, filter, requestId });
	}

	updateFilters(key: string, filters: { [filterKey: string]: IFilter }): void {
		const filtersInstance = this.filterStore.getByKey(key);
		const changedKeys = Object.keys(filters);
		const lastChangedKey = changedKeys[changedKeys.length - 1];

		// Optimistic UI update - update state immediately for instant feedback
		const updatedFilterMap = filtersInstance?.filterMap
			? {
					...filtersInstance.filterMap,
					...changedKeys.reduce(
						(acc, filterKey) => {
							acc[filterKey] = { ...filtersInstance.filterMap[filterKey], items: filters[filterKey].items };
							return acc;
						},
						{} as { [key: string]: IFilter }
					)
				}
			: null;

		// Only skip the request when EVERY changed filter is client-side; a mixed
		// batch still needs the server for its scope filters.
		const allClientSide = changedKeys.length > 0 && changedKeys.every((filterKey) => this.isClientSideFilter(key, filterKey));

		if (updatedFilterMap && allClientSide) {
			this.settleClientSideChange(key, updatedFilterMap, lastChangedKey);
			return;
		}

		// Generate unique request ID to track this specific update
		const requestId = `${key}_${++this.requestIdCounter}`;
		this.latestRequestId.set(key, requestId);

		if (updatedFilterMap) {
			this.filterStore.update(key, { updating: true, updated: false, filterMap: updatedFilterMap, lastChangedKey });
		}

		// Emit the bulk update request to the debounced subject for API call
		this.bulkUpdateFiltersSubject$.next({ key, filters, requestId });
	}

	/**
	 * Seeds a client-side-only filter instance with its selector items and marks it loaded, so its
	 * selectors render without an init HTTP round-trip. For instances whose config has no baseUrl/
	 * updateUrl (fully client-side); selections then stay local (see the updateUrl guard in the
	 * update pipelines). Call after addFilterInstance(key, config, false).
	 */
	seedClientSelectors(key: string, filterMap: { [filterKey: string]: IFilter }): void {
		if (!this.filterStore.getByKey(key)) {
			return;
		}
		this.filterStore.update(key, { filterMap, loaded: true, loading: false });
		this.validateFacetRegistration(key, filterMap);
	}

	updateFilterItem(key: string, filterKey: string, item: IFilterItem): void {
		const filterInstance = this.filterStore.getByKey(key);
		if (!filterInstance?.filterMap) {
			return;
		}

		const filter = filterInstance.filterMap[filterKey];

		if (!filter) {
			return;
		}

		const updatedFilter = {
			...filter,
			items: filter.items.map((i) => (i.id === item.id ? item : i))
		};

		this.updateFilter(key, filterKey, updatedFilter);
	}

	/**
	 * Seeds a synced filter value (the same `fsk-*` store the framework writes when a user makes a selection).
	 * Useful for pre-selecting a synced filter (e.g. school/year from a deep link) before a selector initializes,
	 * so its setup merges the value via the config's syncFilterKeys.
	 */
	setSyncedFilterValue(syncValue: IFilterSyncValue): void {
		this.filterStorageService.setSyncedFilterValue(syncValue);
	}

	resetFilter(key: string): void {
		const filterInstance = this.filterStore.getByKey(key);
		if (!filterInstance) {
			return;
		}

		this.filterStorageService.removeFilter(filterInstance.key);
		this.filterStore.remove(filterInstance.key);

		this.initializeFilter(filterInstance.key);
	}

	reloadFilterOptions(key: string): void {
		const filterInstance = this.filterStore.getByKey(key);
		if (!filterInstance) {
			return;
		}

		const initValues = this.filterStorageService.getInitValues(filterInstance);

		this.initFilterSubject$.next({
			url: `${filterInstance.config.baseUrl}${filterInstance.config.initUrl}`,
			body: initValues,
			key: key
		});
	}

	addFilterInstance(key: string, filterConfig: IFiltersConfig, initialize: boolean = false): void {
		let filtersInstance: IFiltersInstance = {
			key,
			loading: true,
			loaded: false,
			updating: false,
			updated: false,
			config: filterConfig,
			filterMap: null,
			results: [],
			resultsLoaded: false,
			resultsLoading: true,
			lastChangedKey: null
		};

		if (!filterConfig.type) {
			filterConfig.type = 'sidenav';
		}

		if (filterConfig.type === 'chip' && filterConfig.canSwitchView === undefined) {
			filterConfig.canSwitchView = true;
		}

		if (filterConfig.keepSessionState) {
			if (this.filterStorageService.hasFilter(key)) {
				filtersInstance = this.filterStorageService.getFilter(key);
				filtersInstance.config = filterConfig; // Never trust stored config, always overwrite with latest
				// Scrub the excluded keys out of the RESTORED map too, not just the persisted copy.
				// setFilter strips a copy on the way out, so a map written by an older bundle (or by
				// any build predating the exclusion) still carries the key into the store — enough to
				// render a chip for a filter that no longer exists and to post it to `<base>/update`,
				// which 500s on an unknown key. Must run after the config overwrite above: the fresh
				// config is the only one whose exclusion list is trustworthy.
				filtersInstance = this.filterStorageService.withoutExcludedKeys(filtersInstance);
				filtersInstance.loaded = false;
				filtersInstance.loading = false;
				filtersInstance.updated = false;
				filtersInstance.updating = false;
				filtersInstance.results = [];
				filtersInstance.resultsLoaded = false;
				filtersInstance.resultsLoading = true;
			}

			filtersInstance.config.baseUrl = filterConfig.baseUrl;
			filtersInstance.config.results = filterConfig.results;
			filtersInstance.config.initValues = filterConfig.initValues;
			filtersInstance.config.syncFilterKeys = filterConfig.syncFilterKeys;
			this.filterStorageService.setFilter(key, filtersInstance);
		}

		this.filterStore.set({ key, ...filtersInstance });

		if (initialize) {
			this.initializeFilter(key);
		}
	}

	lock(key: string): void {
		const filterInstance = this.filterStore.getByKey(key);
		if (!filterInstance) {
			return;
		}

		const changes = { config: { ...filterInstance.config, locked: true, closed: true } } as Partial<IFiltersInstance>;

		this.filterStore.update(filterInstance.key, changes);
	}

	unlock(key: string): void {
		const filterInstance = this.filterStore.getByKey(key);
		if (!filterInstance) {
			return;
		}

		const changes = { config: { ...filterInstance.config, locked: false } } as Partial<IFiltersInstance>;

		this.filterStore.update(filterInstance.key, changes);
	}

	toggleClosed(key: string): void {
		const filterInstance = this.filterStore.getByKey(key);
		if (!filterInstance) {
			return;
		}

		const changes = { config: { ...filterInstance.config, closed: !filterInstance.config.closed } } as Partial<IFiltersInstance>;

		this.filterStore.update(filterInstance.key, changes);
	}

	toggleExpanded(key: string, filterKey: string): void {
		const filtersInstance = this.filterStore.getByKey(key);
		if (!filtersInstance?.filterMap) {
			return;
		}

		const filter = filtersInstance.filterMap[filterKey];
		if (!filter) {
			return;
		}

		const updatedFilterMap = {
			...filtersInstance.filterMap,
			[filterKey]: {
				...filter,
				expanded: !filter.expanded
			}
		};

		this.filterStore.update(filtersInstance.key, { filterMap: updatedFilterMap });
	}

	setFilterVisibility(key: string, filterKey: string, isVisible: boolean): void {
		const filtersInstance = this.filterStore.getByKey(key);
		if (!filtersInstance?.filterMap) {
			return;
		}

		const filter = filtersInstance.filterMap[filterKey];
		if (!filter) {
			return;
		}

		const updatedFilterMap = {
			...filtersInstance.filterMap,
			[filterKey]: {
				...filter,
				hidden: !isVisible
			}
		};

		this.filterStore.update(filtersInstance.key, { filterMap: updatedFilterMap });
	}

	// Get methods for filter instance properties
	getFilterInstance(key: string): Signal<IFiltersInstance | null> {
		return computed(() => this.filterStore.getByKey(key) ?? null);
	}

	getFilterConfig(key: string): Signal<IFiltersConfig | null> {
		return computed(() => this.filterStore.getByKey(key)?.config ?? null);
	}

	getFilterMap(key: string): Signal<{ [key: string]: IFilter } | null> {
		return computed(() => this.filterStore.getByKey(key)?.filterMap ?? null);
	}

	getVisibleSelectors(key: string): Signal<{ key: string; value: IFilter }[]> {
		return computed(() => {
			const filterMap = this.getFilterMap(key)();
			if (!filterMap) {
				return [];
			}
			return Object.entries(filterMap)
				.filter(
					([_, filter]) =>
						!filter.hidden &&
						filter.items?.length > 0 &&
						(filter.displayLocation === DisplayLocation.Top || filter.displayLocation === DisplayLocation.TopAndSide)
				)
				.map(([key, filter]) => ({ key, value: filter }));
		});
	}

	getVisibleFilters(key: string): Signal<{ key: string; value: IFilter }[]> {
		return computed(() => {
			const filterMap = this.getFilterMap(key)();
			if (!filterMap) {
				return [];
			}
			return Object.entries(filterMap)
				.filter(
					([_, filter]) =>
						!filter.hidden &&
						filter.items?.length > 0 &&
						(filter.displayLocation === DisplayLocation.Side ||
							filter.displayLocation === DisplayLocation.TopAndSide ||
							filter.displayLocation == null)
				)
				.map(([key, filter]) => ({ key, value: filter }));
		});
	}

	getFilterGroupings(key: string): Signal<IFilterGrouping[] | null> {
		return computed(() => this.filterStore.getByKey(key)?.config?.filterGroups ?? null);
	}

	getHasGroups(key: string): Signal<boolean> {
		return computed(() => {
			const groupings = this.filterStore.getByKey(key)?.config?.filterGroups;
			return groupings ? groupings.length > 0 : false;
		});
	}

	getSelectedSelector(key: string): Signal<{ key: string; value: IFilter }[]> {
		return computed(() => {
			const filterMap = this.filterStore.getByKey(key)?.filterMap;
			if (!filterMap) {
				return [];
			}
			const visibleSelectors = Object.entries(filterMap)
				.filter(
					([_, filter]) =>
						!filter.hidden &&
						filter.items?.length > 0 &&
						(filter.displayLocation === DisplayLocation.Top || filter.displayLocation === DisplayLocation.TopAndSide)
				)
				.map(([key, filter]) => ({ key, value: filter }));
			return visibleSelectors.filter((f) => f.value.items?.some((i) => i.selected));
		});
	}

	getSelectedFilters(key: string): Signal<{ key: string; value: IFilter }[]> {
		return computed(() => {
			const filterMap = this.filterStore.getByKey(key)?.filterMap;
			if (!filterMap) {
				return [];
			}
			const visibleFilters = Object.entries(filterMap)
				.filter(
					([_, filter]) =>
						!filter.hidden &&
						filter.items?.length > 0 &&
						(filter.displayLocation === DisplayLocation.Side ||
							filter.displayLocation === DisplayLocation.TopAndSide ||
							filter.displayLocation == null)
				)
				.map(([key, filter]) => ({ key, value: filter }));
			return visibleFilters.filter((f) => f.value.items?.some((i) => i.selected));
		});
	}

	getLoaded(key: string): Signal<boolean> {
		return computed(() => this.filterStore.getByKey(key)?.loaded ?? false);
	}

	getUpdated(key: string): Signal<boolean> {
		return computed(() => this.filterStore.getByKey(key)?.updated ?? false);
	}

	getLoading(key: string): Signal<boolean> {
		return computed(() => this.filterStore.getByKey(key)?.loading ?? false);
	}

	getUpdating(key: string): Signal<boolean> {
		return computed(() => this.filterStore.getByKey(key)?.updating ?? false);
	}

	getClosed(key: string): Signal<boolean> {
		return computed(() => this.filterStore.getByKey(key)?.config.closed ?? false);
	}

	getLocked(key: string): Signal<boolean> {
		return computed(() => this.filterStore.getByKey(key)?.config.locked ?? false);
	}

	getResults(key: string): Signal<unknown[]> {
		return computed(() => this.filterStore.getByKey(key)?.results ?? []);
	}

	/** Test seam and client-mode entry point for pushing rows into the store. */
	setResults(key: string, results: unknown[]): void {
		if (!this.filterStore.getByKey(key)) {
			return;
		}
		this.filterStore.update(key, { results, resultsLoaded: true, resultsLoading: false });
	}

	/**
	 * Rows narrowed by the client-side facets and the base predicate. Falls back
	 * to the raw results when the instance has no clientFacets, so the other
	 * screens' getResults semantics stay untouched.
	 *
	 * The fallback honours `source` before `results`. The tally is null until the filter map
	 * loads, and for an instance that sets `source` the stored results are a DIFFERENT SHAPE
	 * from what it renders (FEL stores one row per student x subject and renders one per
	 * student), so falling straight through to results would hand the caller rows its own
	 * row type does not describe. Ordering makes that window very hard to reach today; the
	 * point is that the two branches agree on the row type either way.
	 */
	getFacetedResults(key: string): Signal<unknown[]> {
		const clientFacets = this.getClientFacetsSignal(key);
		return computed(() => {
			const tally = this.getTally(key)();
			if (tally) {
				return tally.visible;
			}
			return (clientFacets()?.source?.() as unknown[] | undefined) ?? this.getResults(key)();
		});
	}

	/**
	 * Per-item count + disabled for one filter, or `null` when no badge should render.
	 *
	 * Deliberately a map alongside the store rather than an overlay on the stored items:
	 * both outbound payload builders splice `items` straight from what a component hands
	 * back, so anything stamped onto an `IFilterItem` for presentation gets a free ride to
	 * the wire on the next round trip.
	 *
	 * Three cases, and the distinction that matters is `clientFacets` PRESENCE, not whether
	 * a tally happens to be empty:
	 *
	 * - No `clientFacets` (every app but People Management): counts come from the stored
	 *   items, because that is where the server / persisted session put them (e.g. Waitlist
	 *   writes server counts into the store filterMap directly). `disabled` is always false —
	 *   only a client-side tally can know an option would yield nothing.
	 * - `clientFacets` + this filter registered + rows loaded: tally-derived.
	 * - `clientFacets` + this filter NOT registered (School, Academic Year), or rows not yet
	 *   loaded: `null`. Falling back to the stored `count` here would render a literal `0`
	 *   badge on a scope selector, since the server's Count is a non-nullable int.
	 *
	 * Counts are meaningless before rows arrive, and disabling every option on an empty set
	 * would leave the user unable to select anything — so badges suppress on an in-flight
	 * refetch even though the grid keeps showing the last-tallied rows (see getTally /
	 * getFacetedResults, which don't gate on resultsLoaded).
	 */
	getFilterCounts(key: string, filterKey: string): Signal<FilterItemCounts | null> {
		const memoKey = `${key}|${filterKey}`;
		const existing = this.filterCountsByKey.get(memoKey);
		if (existing) {
			return existing;
		}

		const filterMap = this.getFilterMapSignal(key);
		const clientFacets = this.getClientFacetsSignal(key);
		const resultsLoaded = this.getResultsLoadedSignal(key);
		const registeredKeys = this.getRegisteredKeysSignal(key);

		const filterCounts = computed<FilterItemCounts | null>(() => {
			const items = filterMap()?.[filterKey]?.items;
			if (!items) {
				return null;
			}

			if (!clientFacets()) {
				return new Map(items.map((item) => [item.id, { count: item.count, disabled: false }]));
			}

			if (!resultsLoaded() || !registeredKeys().has(filterKey)) {
				return null;
			}

			const tallied = this.getTally(key)()?.counts[filterKey];
			if (!tallied) {
				return null;
			}

			return new Map(
				items.map((item) => {
					const count = tallied.get(item.id) ?? 0;
					return [item.id, { count, disabled: count === 0 && !item.selected }];
				})
			);
		});

		this.filterCountsByKey.set(memoKey, filterCounts);
		return filterCounts;
	}

	private registeredFacetKeys(key: string): Set<string> {
		const groups = this.filterStore.getByKey(key)?.config?.clientFacets?.groups;
		if (!groups) {
			return new Set<string>();
		}
		return new Set(groups.flatMap((group) => facetKeys(group as FacetGroup<unknown>)));
	}

	private isClientSideFilter(key: string, filterKey: string): boolean {
		return this.registeredFacetKeys(key).has(filterKey);
	}

	/**
	 * A client-side facet is evaluated in the browser, so by definition it must never narrow what
	 * the server returns. The count beside an UNSELECTED option is the number of rows the OTHER
	 * facets leave, which is only computable if the browser holds the set before any facet applies;
	 * and a facet deselect issues no request at all, so a server-narrowed set would never widen
	 * back. Both mean the outbound `/results` body has to look as though nothing were selected.
	 *
	 * Applied to EVERY outbound body, not just `/results`: the two `/update` payload builders in the
	 * constructor run it too. `/update` cannot narrow rows, only the option list it returns — but a
	 * FilterDependency that sources from a faceted key would rebuild that list against a client-side
	 * selection, narrowing the options and the counts computed from them. No dependency in either
	 * adopter does that today (SP and PM both source theirs from school / academic year, which stay
	 * server-side), so this is holding the invariant rather than fixing a live defect.
	 *
	 * Returns a copy — the stored map keeps its real selection, which is what drives the tally and
	 * the checked boxes in the UI. Only the request body is suppressed; handleFilterResponse
	 * re-imposes the local selection onto whatever comes back, so a suppressed body cannot clear a box.
	 */
	private withoutFacetSelections(filterMap: { [key: string]: IFilter }, clientSideKeys: Set<string>): { [key: string]: IFilter } {
		if (clientSideKeys.size === 0) {
			return filterMap;
		}

		const outbound = { ...filterMap };

		clientSideKeys.forEach((filterKey) => {
			const filter = outbound[filterKey];
			if (!filter) {
				return;
			}

			outbound[filterKey] = { ...filter, items: filter.items.map((item) => (item.selected ? { ...item, selected: false } : item)) };
		});

		return outbound;
	}

	/**
	 * Dev-only sanity check that a `clientFacets` registry actually lines up with the
	 * filter keys the instance ended up with. Nothing else catches this: facet specs
	 * call predicates directly with correct literals, so a typo'd or renamed key stays
	 * green in tests while silently falling back to the server path — and the
	 * server-side facet filtering has been deleted, so that filter now matches every
	 * row instead of none. Matching everything reads as "the filter is being ignored"
	 * and gets tolerated far longer than matching nothing (which reads as broken and
	 * gets reported immediately).
	 *
	 * Runs once per instance, the first time `clientFacets` and a populated filterMap
	 * are both known (from seedClientSelectors or handleFilterResponse) — not from
	 * inside getTally/getFilterCounts, which recompute on every click and would
	 * spam the console instead of logging once at setup.
	 */
	private validateFacetRegistration(key: string, filterMap: { [filterKey: string]: IFilter } | null): void {
		if (!isDevMode() || !filterMap || this.validatedFacetKeys.has(key)) {
			return;
		}
		const clientFacetsConfig = this.filterStore.getByKey(key)?.config?.clientFacets;
		const groups = clientFacetsConfig?.groups as FacetGroup<unknown>[] | undefined;
		// An empty (not absent) groups array is a legitimate config — e.g. the PM shell
		// unconditionally sets `clientFacets = { groups: this.facets() ?? [], base: ... }` so a
		// queue with no facets still gets the base search predicate. Falling through to the
		// Side-filter loop below with an empty `seenKeys` set would warn on every Side filter.
		if (!groups?.length) {
			return;
		}
		this.validatedFacetKeys.add(key);

		const availableKeys = Object.keys(filterMap);
		const seenKeys = new Set<string>();
		const serverSideKeys = new Set(clientFacetsConfig?.serverSideKeys ?? []);

		for (const group of groups) {
			for (const facetKey of facetKeys(group)) {
				if (!availableKeys.includes(facetKey)) {
					console.error(
						`[FilterStateService] Instance "${key}": registered facet key "${facetKey}" does not exist in its filterMap. Available keys: ${availableKeys.join(', ') || '(none)'}`
					);
				}
				if (seenKeys.has(facetKey)) {
					console.error(
						`[FilterStateService] Instance "${key}": facet key "${facetKey}" is registered in more than one group — its count would be double-bumped.`
					);
				}
				seenKeys.add(facetKey);
			}
		}

		for (const [filterKey, filter] of Object.entries(filterMap)) {
			if (filter.displayLocation === DisplayLocation.Side && !seenKeys.has(filterKey) && !serverSideKeys.has(filterKey)) {
				console.warn(
					`[FilterStateService] Instance "${key}": filter "${filterKey}" is Side-located but has no registered client-side facet — its server-side predicate may have been removed.`
				);
			}
		}
	}

	/**
	 * A client-side change makes no request, so it must do the three things the
	 * HTTP response path normally does: clear the loading flags, record what
	 * changed, and persist.
	 */
	private settleClientSideChange(key: string, filterMap: { [filterKey: string]: IFilter }, lastChangedKey: string): void {
		// A facet click can land while a prior scope change (e.g. a school switch) is still
		// debouncing or awaiting its response. Forcing `updating` false here would drop the
		// shell's loader before that request settles — handleFilterResponse is what actually
		// clears it — so leave the flag as-is when a request is already outstanding for this key.
		const updating = this.filterStore.getByKey(key)?.updating ?? false;

		// KNOWN GAP — `updated` does not signal repeat changes on this path.
		//
		// Consumers watch for `updated` to go false→true; the RISING EDGE is the signal, not
		// the value. The server path gets one for free: updateFilter writes false optimistically
		// and handleFilterResponse writes true after the round trip, so the two writes are
		// genuinely separated. Here there is no round trip, and `updated` is already true from
		// the previous click — so nothing transitions and subscribers never fire. The first
		// click after init works (it starts false); every click after that is silent.
		//
		// Writing false-then-true here does NOT fix it: signals only notify when a *pulled*
		// value differs from the last pull, so two writes with no yield between them are
		// observed exactly once, as the final value. Forcing a flush between them (e.g.
		// ApplicationRef.tick) was tried and reverted — it costs a full application change
		// detection pass per facet click, throws when called re-entrantly, and swallowing that
		// throw makes the gap silently return.
		//
		// The fix is to stop overloading a boolean as a change notification: add a monotonic
		// `changeSequence: number` to IFiltersInstance, increment it on every settle (client
		// AND server), and expose getChangeSequence(key) for consumers to watch. A counter
		// cannot get stuck the way an already-true boolean can, needs no framework coupling,
		// and fails visibly rather than silently.
		//
		// The counter alone changes nothing, though: `updated` stays exactly as stuck as it is
		// today unless every consumer stops reading it. That means migrating each of the twelve
		// `toObservable(getUpdated(key)).pipe(filter(Boolean))` call sites over to
		// getChangeSequence(key) (a distinctSince/pairwise-style comparison, not a Boolean
		// filter) — lockers' `loaded`-gating one included. That migration, not adding the
		// counter itself, is the bulk of the work here.
		//
		// Deferred deliberately: neither clientFacets adopter reads this. People Management does
		// not, and Special Populations was checked when it adopted — ILP and FEL read neither
		// getUpdated nor getLastChangedKey, driving their grids off getFacetedResults instead.
		// CHECK THIS AGAIN before the next adopter: twelve screens use
		// `toObservable(getUpdated(key)).pipe(filter(Boolean))`, and lockers gates its entire
		// `loaded` state on it. An adopter that reads getUpdated would render once and then go
		// stale with no error.
		const updated = this.filterStore.setFilterInstance(key, {
			filterMap,
			updating,
			updated: true,
			lastChangedKey
		});

		if (updated.config.keepSessionState) {
			// Excludes results: see the note on handleFilterResponse's setFilter call below.
			this.filterStorageService.setFilter(key, { ...updated, results: [] });
		}
	}

	/**
	 * Narrow signals over the slices getTally/getFilterCounts actually read.
	 * `filterStore.getByKey(key)` returns a new entity object on ANY store mutation for
	 * this key (toggleClosed, toggleExpanded, setFilterVisibility, lock/unlock included),
	 * so reading it directly inside a computed would re-run tallyFacets - and mint a new
	 * `visible` array reference - on every unrelated UI-state change. Reading through these
	 * per-slice computeds instead means the tally/filterMapWithCounts computeds only
	 * recompute when one of the slices itself changes reference.
	 */
	private getResultsSignal(key: string): Signal<unknown[]> {
		return computed(() => this.filterStore.getByKey(key)?.results ?? []);
	}

	private getFilterMapSignal(key: string): Signal<{ [filterKey: string]: IFilter } | null> {
		return computed(() => this.filterStore.getByKey(key)?.filterMap ?? null);
	}

	private getClientFacetsSignal(key: string): Signal<IFiltersConfig['clientFacets'] | null> {
		return computed(() => this.filterStore.getByKey(key)?.config?.clientFacets ?? null);
	}

	private getResultsLoadedSignal(key: string): Signal<boolean> {
		return computed(() => this.filterStore.getByKey(key)?.resultsLoaded ?? false);
	}

	/**
	 * Narrow signal over the same slice as getClientFacetsSignal, for the same reason as the
	 * other slice computeds above: registeredFacetKeys() reads `filterStore.getByKey(key)`
	 * directly, which depends on the entity map as a whole, so calling it from inside
	 * getFilterCounts's computed re-ran (and re-allocated) that computed on every
	 * toggleClosed/toggleExpanded/lock/setFilterVisibility for any instance, not just this key.
	 */
	private getRegisteredKeysSignal(key: string): Signal<Set<string>> {
		const clientFacets = this.getClientFacetsSignal(key);
		return computed(() => {
			const groups = clientFacets()?.groups;
			if (!groups) {
				return new Set<string>();
			}
			return new Set(groups.flatMap((group) => facetKeys(group as FacetGroup<unknown>)));
		});
	}

	private getTally(key: string): Signal<TallyResult<unknown> | null> {
		const existing = this.tallyByKey.get(key);
		if (existing) {
			return existing;
		}

		const results = this.getResultsSignal(key);
		const filterMap = this.getFilterMapSignal(key);
		const clientFacets = this.getClientFacetsSignal(key);

		// The base predicate is applied HERE rather than passed into tallyFacets, and this
		// split is load-bearing for performance. A base predicate depends only on the rows
		// and its own inputs (for PM, the search term) — never on facet selections — but the
		// tally below depends on filterMap, so every filter click invalidates it. Evaluating
		// base inside that computed re-ran it per row per click: PM's base is a Levenshtein
		// fuzzy match, so a search term active on a 1200-row campus meant 1200 fuzzy matches
		// on every checkbox toggle. Keeping it in its own computed means a selection change
		// leaves it cached, and it only recomputes when rows or the search term actually change.
		const baseFilteredRows = computed(() => {
			const facets = clientFacets();
			// A `source` signal wins over the stored results: see IClientFacetsConfig.source.
			const rows = (facets?.source?.() as unknown[] | undefined) ?? results();
			const base = facets?.base?.() ?? null;
			return base ? rows.filter(base) : rows;
		});

		const tally = computed(() => {
			const facets = clientFacets();
			const map = filterMap();
			if (!facets || !map) {
				return null;
			}

			const selections = Object.entries(map).reduce((acc, [filterKey, filter]) => {
				acc[filterKey] = filter.items.filter((i) => i.selected).map((i) => i.id);
				return acc;
			}, {} as FacetSelections);

			// No base argument: baseFilteredRows already applied it.
			return tallyFacets(baseFilteredRows(), facets.groups as FacetGroup<unknown>[], selections);
		});

		this.tallyByKey.set(key, tally);
		return tally;
	}

	getResultsLoaded(key: string): Signal<boolean> {
		return computed(() => this.filterStore.getByKey(key)?.resultsLoaded ?? false);
	}

	getResultsLoading(key: string): Signal<boolean> {
		return computed(() => this.filterStore.getByKey(key)?.resultsLoading ?? false);
	}

	getLastChangedKey(key: string): Signal<string | null> {
		return computed(() => this.filterStore.getByKey(key)?.lastChangedKey ?? null);
	}

	getFilterType(key: string): Signal<'sidenav' | 'chip' | null> {
		return computed(() => this.filterStore.getByKey(key)?.config.type ?? null);
	}

	getFilterItems(key: string, filterKey: string): Signal<IFilterItem[]> {
		return computed(() => this.filterStore.getFilterMap(key)?.[filterKey]?.items);
	}

	getFilter(key: string, filterKey: string): Signal<IFilter | null> {
		return computed(() => {
			const filterMap = this.filterStore.getFilterMap(key);
			if (!filterMap) {
				return null;
			}

			const filter: IFilter = filterMap[filterKey];

			if (!filter) {
				return null;
			}
			return filter;
		});
	}

	getFilterSelectedValue(key: string, filterKey: string): Signal<number> {
		return computed(() => {
			const items = this.filterStore.getFilterMap(key)?.[filterKey]?.items;
			if (!items) {
				return null;
			}
			return items.find((i) => i.selected)?.id ?? null;
		});
	}

	getFilterSelectedValues(key: string, filterKey: string): Signal<number[]> {
		return computed(() => {
			const items = this.filterStore.getFilterMap(key)?.[filterKey]?.items;
			if (!items) {
				return [];
			}
			return items.filter((i) => i.selected).map((i) => i.id);
		});
	}

	getFilterSelectedItem(key: string, filterKey: string): Signal<IFilterItem | null> {
		return computed(() => {
			const filter = this.filterStore.getFilterMap(key)?.[filterKey];

			if (!filter) {
				return null;
			}

			return filter?.items.find((i) => i.selected) ?? null;
		});
	}

	getFilterSelections(key: string): Signal<{ [key: string]: IFilter }> {
		return computed(() => {
			const filterMap = this.filterStore.getFilterMap(key);
			if (!filterMap) {
				return {};
			}

			return Object.fromEntries(
				Object.entries(filterMap)
					.filter(([, filter]) => filter.items?.some((i) => i.selected))
					.map(([key, filter]) => [
						key,
						{
							...filter,
							items: filter.items.filter((i) => i.selected)
						}
					])
			);
		});
	}

	getFilterSelectionCounts(key: string): Signal<{ key: string; count: number }[]> {
		return computed(() => {
			const filterMap = this.filterStore.getFilterMap(key);
			if (!filterMap) {
				return [];
			}

			return Object.entries(filterMap).map(([key, filter]) => ({
				key,
				count: filter.items.filter((i) => i.selected).length
			}));
		});
	}

	private handleFilterResponse(filterMap: { [key: string]: IFilter }, key: string, requestId?: string): void {
		const filtersInstance = this.filterStore.getByKey(key);
		if (!filtersInstance) {
			return;
		}

		const clientSideKeys = this.registeredFacetKeys(key);

		// Preserve client-side expanded state from existing filterMap
		if (filtersInstance.filterMap) {
			Object.keys(filterMap).forEach((filterKey) => {
				const previous = filtersInstance.filterMap[filterKey];
				if (!previous) {
					return;
				}

				filterMap[filterKey] = { ...filterMap[filterKey], expanded: previous.expanded };

				// For a registered facet the local selection wins over any response —
				// per-filter, bulk, or init — because a client-side click issues no
				// request of its own, and a snapshot taken before it would otherwise
				// silently revert it.
				if (clientSideKeys.has(filterKey)) {
					const selectedIds = new Set(previous.items.filter((i) => i.selected).map((i) => i.id));
					filterMap[filterKey] = {
						...filterMap[filterKey],
						items: filterMap[filterKey].items.map((item) => ({ ...item, selected: selectedIds.has(item.id) }))
					};
				}
			});
		}

		const shouldFetchResults = filtersInstance.config.resultsUrl != null;

		// Update state with filterMap and resultsLoading flag if needed
		const changes = {
			filterMap: filterMap,
			updated: true,
			updating: false,
			loaded: true,
			loading: false,
			resultsLoading: shouldFetchResults, // Set loading state immediately if we're fetching results
			resultsLoaded: !shouldFetchResults // Only mark loaded if we're not fetching results
		} as Partial<IFiltersInstance>;

		const updatedFilterInstance = this.filterStore.setFilterInstance(key, changes);
		this.validateFacetRegistration(key, filterMap);

		if (updatedFilterInstance.config.keepSessionState) {
			// Same over-persistence as settleClientSideChange: `changes` never touches `results`,
			// so this would otherwise serialize whatever rows were already in the store (stale or
			// current) on every filter response. addFilterInstance() always zeroes results/
			// resultsLoaded/resultsLoading when restoring from storage, so nothing reads it back.
			this.filterStorageService.setFilter(key, { ...updatedFilterInstance, results: [] });
		}

		// Fetch results in parallel if needed - start immediately without waiting
		if (shouldFetchResults) {
			this.http
				.post<unknown[]>(
					`${updatedFilterInstance.config.baseUrl}${updatedFilterInstance.config.resultsUrl}`,
					this.withoutFacetSelections(filterMap, clientSideKeys)
				)
				.pipe(take(1))
				.subscribe({
					next: (results) => {
						// Only process results if this is still the latest request (prevent stale results)
						if (requestId && this.latestRequestId.get(key) !== requestId) {
							return;
						}

						if (updatedFilterInstance.config.results) {
							updatedFilterInstance.config.results(results);
						}

						this.filterStore.update(updatedFilterInstance.key, { results: results, resultsLoaded: true, resultsLoading: false });
					},
					error: (error) => {
						// Only process error if this is still the latest request
						if (requestId && this.latestRequestId.get(key) !== requestId) {
							return;
						}

						console.error('Error fetching filter results:', error);
						this.snackbarService.error('Failed to load filter results. Please try again.');
						this.filterStore.update(updatedFilterInstance.key, { resultsLoaded: false, resultsLoading: false });
					}
				});
		}

		// Handle synced filter values
		Object.entries(filterMap)
			.filter(([_, filter]) => filter.syncKey?.length > 0)
			.forEach(([key, filter]) => {
				const selectedItems = filter.items.filter((i) => i.selected).map((i) => i.id);

				if (selectedItems.length === 0) {
					return;
				}

				this.filterStorageService.setSyncedFilterValue({
					syncKey: filter.syncKey,
					filterKey: key,
					value: filter.multiple ? selectedItems : selectedItems[0]
				});
			});
	}
}
